import { Download, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useDevelopmentFixtures } from "../../api/client";
import { Button } from "../../components/ui";
import { commerceApi, type BookingDocumentKind } from "../../data/workspace";

type Summary = Record<string, unknown> | null | undefined;

const labels: Record<BookingDocumentKind, string> = {
  apartado: "Solicitud de apartado",
  "apartado-recibido": "Apartado confirmado",
  confirmacion: "Confirmación con pagos",
  recibo: "Comprobante de pago",
};

/** Which documents fit the booking: the fee request until it is paid, then its receipt; tours and schools get the confirmation. */
export function paymentDocumentKinds(service: string | null | undefined, summary: Summary): BookingDocumentKind[] {
  const terms = (summary?.payment_terms ?? null) as { kind?: string } | null;
  const fee = Number(summary?.reservation_fee ?? 0);
  const feePaid = Number(summary?.reservation_fee_paid ?? 0);
  const kinds: BookingDocumentKind[] = [];
  if (terms?.kind === "reservation_fee" || fee > 0) kinds.push(feePaid > 0 && feePaid >= fee ? "apartado-recibido" : "apartado");
  if (service === "tour" || service === "school") kinds.push("confirmacion");
  // A receipt of the latest payment recorded from the panel (cash or transfer), for any service.
  if (Array.isArray(summary?.manual_payments) && summary.manual_payments.length > 0) kinds.push("recibo");
  return kinds;
}

/** Download or send by WhatsApp the payment documents of a booking. */
export function PaymentDocuments({ bookingId, service, summary, conversationId, canSend, withoutConfirmation = false }: {
  bookingId: string;
  service: string | null | undefined;
  summary: Summary;
  conversationId: string | null;
  canSend: boolean;
  /** The case has its full confirmation (from the quote), so the short one is not offered too. */
  withoutConfirmation?: boolean;
}) {
  const [busy, setBusy] = useState("");
  const [feedback, setFeedback] = useState("");
  const kinds = paymentDocumentKinds(service, summary).filter((kind) => !(withoutConfirmation && kind === "confirmacion"));
  if (!kinds.length) return null;
  const run = async (key: string, task: () => Promise<unknown>, done: string) => {
    setBusy(key);
    setFeedback("");
    try {
      if (!useDevelopmentFixtures) await task();
      setFeedback(done);
    } catch (error) {
      setFeedback(error instanceof Error ? error.message : "No se pudo completar.");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="payment-docs">
      <h3>Documentos para el cliente</h3>
      {kinds.map((kind) => (
        <div key={kind} className="payment-docs__row">
          <strong>{labels[kind]}</strong>
          <div className="case-actions">
            <Button variant="secondary" icon={Download} disabled={!!busy} onClick={() => void run(`${kind}:pdf`, () => commerceApi.downloadBookingDocument(bookingId, kind), "PDF descargado.")}>
              PDF
            </Button>
            {canSend && conversationId && (
              <Button icon={MessageCircle} aria-label={`Enviar ${labels[kind]} por WhatsApp`} disabled={!!busy} onClick={() => void run(`${kind}:wa`, () => commerceApi.sendBookingDocument(bookingId, kind, conversationId), `${labels[kind]} enviado por WhatsApp.`)}>
                {busy === `${kind}:wa` ? "Enviando…" : "WhatsApp"}
              </Button>
            )}
          </div>
        </div>
      ))}
      {feedback && <p className="quote-feedback" role="status">{feedback}</p>}
    </div>
  );
}
